import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { DownloadIcon, XIcon } from 'lucide-react';
import { chapters, storyboardFrames, storyboardMeta } from '../../data/journey';
import { ScreenThumb } from './ScreenThumb';
interface StoryboardOverlayProps {
  onClose: () => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;
const ACCENT: Record<number, string> = {
  0: '#5F7789',
  1: '#1E76CF',
  2: '#4C7C9B',
  3: '#203864'
};
const sections = [{
  snak: 0 as const,
  label: storyboardMeta.navLabel,
  archetype: storyboardMeta.navArchetype,
  meta: '',
  goal: storyboardMeta.navGoal
}, ...chapters.map(chapter => ({
  snak: chapter.snak,
  label: chapter.label,
  archetype: chapter.archetype,
  meta: `${chapter.verb} · ${chapter.differentiation} · ${chapter.brandNote}`,
  goal: chapter.goal
}))];
function CommentBox() {
  return <div className="mt-2 rounded-md border border-line" data-id="element-865" data-mp="3iiil1an8elp8">
      <p className="border-b border-line px-2 py-1 text-[7.5px] uppercase tracking-micro text-mut" data-id="element-866" data-mp="3igxr9n8qr6sf">
        {storyboardMeta.commentLabel}
      </p>
      <div className="flex flex-col gap-3 px-2 py-2.5" data-id="element-867" data-mp="8ttjmd7rz2f8">
        {[0, 1, 2].map(line => <span key={line} className="block h-px w-full bg-line" aria-hidden="true" data-id="element-868" data-mp="4shsg399694x" />)}
      </div>
    </div>;
}
export function StoryboardOverlay({
  onClose
}: StoryboardOverlayProps) {
  useEffect(() => {
    document.body.classList.add('printing-storyboard');
    return () => document.body.classList.remove('printing-storyboard');
  }, []);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2,
    ease: EASE
  }} className="storyboard-overlay fixed inset-0 z-50 overflow-y-auto bg-navy/40 px-4 py-6 sm:px-6" role="dialog" aria-modal="true" aria-label={storyboardMeta.title} data-id="element-869" data-mp="3m74xrmaob6oz">
      <div className="storyboard-sheet mx-auto h-fit w-full max-w-5xl overflow-hidden rounded-2xl bg-paper shadow-soft" data-id="element-870" data-mp="33a3qtvxk6wie">
        <header className="flex items-start justify-between gap-4 border-b border-line px-8 py-6" data-id="element-871" data-mp="2tlyiqa0o6nbr">
          <div data-id="element-872" data-mp="2ymm52idyefzn">
            <p className="text-[9px] uppercase tracking-micro text-blue" data-id="element-873" data-mp="2yn5oy4n5zegb">
              {storyboardMeta.eyebrow}
            </p>
            <h2 className="mt-2 font-display text-[22px] font-semibold leading-snug text-navy" data-id="element-874" data-mp="2onolltvdxcni">
              {storyboardMeta.title}
            </h2>
            <p className="mt-1 text-[12.5px] text-mut" data-id="element-875" data-mp="3cek76998zbmb">{storyboardMeta.subtitle}</p>

            <div className="mt-4 flex flex-wrap gap-6" data-id="element-876" data-mp="3hbanmxoys989">
              {storyboardMeta.reviewerFields.map(field => <div key={field} className="w-40" data-id="element-877" data-mp="32mwmqsb3yuuc">
                  <span className="block h-px w-full bg-line" aria-hidden="true" data-id="element-878" data-mp="37m9b5hhmo9z1" />
                  <span className="mt-1 block text-[8px] uppercase tracking-micro text-mut" data-id="element-879" data-mp="37k6ck92et8ip">
                    {field}
                  </span>
                </div>)}
            </div>
          </div>

          <div className="no-print flex shrink-0 items-center gap-2" data-id="element-880" data-mp="1onl4ejd03hec">
            <button type="button" onClick={() => window.print()} className="flex h-10 items-center gap-2 rounded-full bg-navy px-4 text-[12.5px] font-semibold text-paper transition-opacity duration-150 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-2" data-id="element-881" data-mp="1olry556auz6v">
              <DownloadIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-882" data-mp="15ps31li0zfj2" />
              {storyboardMeta.printLabel}
            </button>
            <button type="button" onClick={onClose} aria-label="Close storyboard" className="flex h-10 w-10 items-center justify-center rounded-full border border-line text-mut transition-colors duration-150 hover:border-mut hover:text-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-883" data-mp="10s8oekhf10ql">
              <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-884" data-mp="2m2yzq5h4exsx" />
            </button>
          </div>
        </header>

        <p className="no-print border-b border-line bg-mist px-8 py-2 text-[11px] text-mut" data-id="element-885" data-mp="3hfi1qt85reb1">
          {storyboardMeta.printHint}
        </p>

        <div className="px-8 py-7" data-id="element-886" data-mp="3qdty7bvph6be">
          {sections.map((section, chapterIndex) => {
          const frames = storyboardFrames.filter(f => f.snak === section.snak);
          return <section key={section.snak} className={`sb-section ${chapterIndex === 0 ? 'sb-section-first' : 'mt-10'}`} data-id="element-887" data-mp="47oo0tp55o13" data-mpi={chapterIndex}>
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-lg px-4 py-2.5" style={{
              backgroundColor: ACCENT[section.snak]
            }} data-id="element-888" data-mp="cz72n06fvvs2">
                  <span className="font-display text-[14px] font-bold uppercase tracking-tight text-paper" data-id="element-889" data-mp="mxd095l7qfr4">
                    {section.label} · {section.archetype}
                  </span>
                  {section.meta && <span className="text-[10px] uppercase tracking-micro text-paper/70" data-id="element-890" data-mp="2ces3ijv67nxx">
                      {section.meta}
                    </span>}
                </div>

                <p className="mt-2 text-[11.5px] leading-snug text-mut" data-id="element-891" data-mp="22fj59et9vn5r">
                  {section.goal} <span className="text-navy2" data-id="element-892" data-mp="1xhrbef92hqyx">{frames.length} pages.</span>
                </p>

                <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-893" data-mp="2v5jc3j7c3ab9">
                  {frames.map((frame, i) => <article key={frame.id} className="sb-frame flex flex-col rounded-xl border border-line p-3" data-id="element-894" data-mp="2q78298h1gzt1" data-mpi={i}>
                      <div className="flex items-baseline gap-2" data-id="element-895" data-mp="2ggvk1mwp5iq2">
                        <span className="font-display text-[11px] font-bold tabular-nums" style={{
                    color: ACCENT[section.snak]
                  }} data-id="element-896" data-mp="3dvhhtsnnsrbq">
                          {section.snak === 0 ? 'E' : section.snak}.{i + 1}
                        </span>
                        <h3 className="flex-1 text-[12px] font-semibold leading-snug text-navy" data-id="element-897" data-mp="1c62768htd0k1">
                          {frame.title}
                        </h3>
                      </div>
                      {frame.layer && <span className="mt-1 self-start rounded-full border px-1.5 py-0.5 text-[7.5px] uppercase tracking-micro" style={{
                  color: ACCENT[section.snak],
                  borderColor: ACCENT[section.snak]
                }} data-id="element-898" data-mp="12f7k51wqtwxj">
                          {frame.layer}
                        </span>}

                      <div className="mt-2 flex justify-center" data-id="element-899" data-mp="28wueagmrri9o">
                        <ScreenThumb id={frame.id} snak={frame.snak} width={148} data-id="element-900" data-mp="28uhx45ou5bxo" />
                      </div>

                      <p className="mt-2.5 text-[10px] leading-snug text-mut" data-id="element-901" data-mp="2dtbbe0by56yw">{frame.purpose}</p>

                      <div className="mt-auto" data-id="element-902" data-mp="25zg8xr9x8wf">
                        <CommentBox data-id="element-903" data-mp="3tfofrr1ttypx" />
                      </div>
                    </article>)}
                </div>
              </section>;
        })}

          <p className="mt-8 border-t border-line pt-4 text-[9px] leading-snug text-mut" data-id="element-904" data-mp="76viz7zw2p09">
            {storyboardMeta.footer}
          </p>

          <button type="button" onClick={onClose} className="no-print mt-6 flex h-12 w-full items-center justify-center rounded-full border border-line text-[13px] font-medium text-navy transition-colors duration-150 hover:border-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-905" data-mp="kx8tni460hta">
            Close storyboard
          </button>
        </div>
      </div>
    </motion.div>;
}